import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { IResourceSelectorSettings } from '../../../viewmodels/AppViewModels';
import { DeprecatedSelect, ISelectOption } from '../../../web/components/DeprecatedSelect';
import { asModalComponent } from '../../../web/components/Modal';
import { TextInput } from '../../../web/components/TextInput';
import { RoundAddIcon } from '../../../web/components/svgs/icons/RoundAddIcon';
import { XIcon } from '../../../web/components/svgs/icons/XIcon';
import { charmGray } from '../../../web/styles/colors';
import { CustomCardDaysOptions } from '../../containers/account/accountTabs/Settings/options';
import { TCustomSettings } from '../../containers/account/accountTabs/Settings/presentation';
import { styleSheet } from './styles';

interface IProps extends IModalContext {
	className?: string;
	onSubmittedValues?(values: ISubmittedValues, card: TCustomSettings): void;
	card: IResourceSelectorSettings;
	cardType?: TCustomSettings;
}
interface InputValue {
	value: string;
}

export interface ISubmittedValues {
	daysToLookAhead: number;
	displayName: string;
	phrases: string[];
}

const CreateCustomDashboardCard = ({ card, cardType, parentModal: { onRequestClose }, onSubmittedValues }: IProps) => {
	const [displayName, setDisplayName] = React.useState(card?.displayName || '');

	const [phrases, setPhrases] = React.useState<InputValue[]>(
		card?.phrases ? card.phrases.map(x => ({ value: x })) : [{ value: '' }]
	);
	const isDisabled = !displayName.length || !phrases[0].value.length;

	const [selectedOption, setSelectedOption] = React.useState<ISelectOption<number>>(getSelectedOption());

	const onNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setDisplayName(e.target.value);
	};

	const onSelectionChange = (option: ISelectOption<number>) => {
		const days = option;
		setSelectedOption(days);
	};

	const handleAddPhrase = () => {
		const newState = [...phrases, { value: '' }];
		setPhrases(newState);
	};

	const handlePhraseChanged = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
		const nextState = [...phrases];
		nextState[idx].value = e.target.value;
		setPhrases(nextState);
	};

	const handleRemovePhrase = (idx: number) => {
		const nextState = [...phrases];
		nextState.splice(idx, 1);
		setPhrases(nextState);
	};

	const handleSaveClick = () => {
		const submittedValues: ISubmittedValues = {
			daysToLookAhead: selectedOption.dataContext,
			displayName,
			phrases: [],
		};
		const values = phrases.filter(x => x.value?.length > 0);

		values.forEach(x => {
			submittedValues.phrases.push(x.value);
		});

		onSubmittedValues(submittedValues, cardType ?? null);
		onRequestClose();
	};

	function getSelectedOption() {
		if (card) {
			const days = card.daysToLookAhead || 60;
			return CustomCardDaysOptions.find(x => x.dataContext === days);
		}
		return CustomCardDaysOptions[1];
	}

	return (
		<article className={css(styleSheet.container)}>
			<h3 className={css(styleSheet.header)}>{`${card ? 'Edit' : 'Create'} Custom Dashboard Card`}</h3>
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='customCardName' className={css(styleSheet.label)}>
					Card Name
				</label>
				<TextInput
					inputClassName=''
					inputId='customCardName'
					onChange={onNameChanged}
					type='text'
					value={displayName || ''}
					placeholder='Add Name'
				/>
			</div>
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='lookAhead' className={css(styleSheet.label)}>
					Look Ahead
				</label>
				<DeprecatedSelect
					onOptionClick={onSelectionChange}
					options={CustomCardDaysOptions}
					selectedOption={selectedOption}
					styles={[styleSheet.dropdownSelect]}
				/>
			</div>
			<div className={css(styleSheet.formControl)}>
				<label htmlFor='lookAhead' className={css(styleSheet.label)}>
					Phrase
				</label>
				{phrases.map((x: InputValue, idx) => {
					const isFirstInput = idx === 0;
					return (
						<div key={idx} className={css(styleSheet.formControl)}>
							<div className={css(styleSheet.formControlInputWrap)}>
								<TextInput
									inputClassName=''
									inputId={`customCardPhrase${idx}`}
									onChange={e => handlePhraseChanged(e, idx)}
									type='text'
									value={x.value || ''}
									placeholder='Add Key Fact Phrase'
									required={idx === 0}
								/>
								{!isFirstInput ? (
									<button onClick={() => handleRemovePhrase(idx)}>
										<XIcon color={charmGray} height={12} width={12} />
									</button>
								) : null}
							</div>
							{isFirstInput ? <small>Add key fact phrase(s) to include in this dashboard card view</small> : null}
						</div>
					);
				})}
				{phrases.length < 5 ? (
					<button className={css(styleSheet.addMore)} onClick={handleAddPhrase}>
						<RoundAddIcon />
						Add Key Fact Phrase
					</button>
				) : null}
			</div>
			<footer className={css(styleSheet.footer)}>
				<button className='cta-button' disabled={isDisabled} onClick={!isDisabled ? handleSaveClick : undefined}>
					Save
				</button>
				<button className='cta-button-reverse' onClick={onRequestClose}>
					Cancel
				</button>
			</footer>
		</article>
	);
};
const CreateCustomDashboardCardAsObserver = observer(CreateCustomDashboardCard);
const CreateCustomDashboardCardWithContext = inject(ModalChildComponentContextKey)(CreateCustomDashboardCardAsObserver);

export const CreateCustomDashboardCardModal = asModalComponent(CreateCustomDashboardCardWithContext, {
	className: 'create-custom-dashboard-card-modal',
	useDefaultHeader: true,
});
